import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Zap, Volume2, VolumeX } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BEAST_LEVELS = {
  warming_up: { color: "text-blue-400", bg: "bg-blue-500", label: "WARMING UP", emoji: "🔥" },
  building: { color: "text-yellow-400", bg: "bg-yellow-500", label: "BUILDING HEAT", emoji: "🔥🔥" },
  on_fire: { color: "text-orange-500", bg: "bg-orange-500", label: "ON FIRE!", emoji: "🔥🔥🔥" },
  BEAST_MODE: { color: "text-red-500", bg: "bg-red-600", label: "BEAST MODE!", emoji: "💀🔥💀" }
};

export default function BeastModeMeter({ 
  onBeastModeActivated, 
  exerciseName = "",
  repsCompleted = 0,
  autoIncrement = false 
}) {
  const [intensity, setIntensity] = useState(0);
  const [level, setLevel] = useState("warming_up");
  const [beastModeActive, setBeastModeActive] = useState(false);
  const [message, setMessage] = useState("");
  const [audioEnabled, setAudioEnabled] = useState(true);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (autoIncrement) {
      intervalRef.current = setInterval(() => {
        setIntensity(prev => {
          const newValue = Math.min(100, prev + Math.random() * 3 + 1);
          return newValue;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoIncrement]);

  useEffect(() => {
    // Determine level based on intensity
    let newLevel = "warming_up";
    if (intensity >= 85) newLevel = "BEAST_MODE";
    else if (intensity >= 60) newLevel = "on_fire";
    else if (intensity >= 30) newLevel = "building";
    
    if (newLevel !== level) {
      setLevel(newLevel);
      
      // Trigger beast mode callback
      if (newLevel === "BEAST_MODE" && !beastModeActive) {
        setBeastModeActive(true);
        if (onBeastModeActivated) onBeastModeActivated();
        playBeastModeVoice();
      }
    }
    
    // Update backend
    updateBeastMeter();
  }, [intensity]);

  const updateBeastMeter = async () => {
    try {
      const response = await axios.post(`${API}/workout/beast-meter`, {
        intensity,
        exercise_name: exerciseName,
        reps_completed: repsCompleted,
        beast_mode_active: beastModeActive
      });
      setMessage(response.data.message);
    } catch (error) {
      console.error("Beast meter update failed:", error);
    }
  };

  const playBeastModeVoice = async () => {
    if (!audioEnabled) return;
    
    try {
      const response = await axios.post(`${API}/voice/coaching`, {
        context: "beast_mode"
      });
      
      if (response.data.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
        audioRef.current = audio;
        audio.play();
      }
    } catch (error) {
      console.error("Voice generation failed:", error);
    }
  };

  const incrementIntensity = (amount = 5) => {
    setIntensity(prev => Math.min(100, prev + amount));
  };

  const levelConfig = BEAST_LEVELS[level];

  return (
    <div className="relative">
      {/* Beast Mode Meter */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Flame className={`h-5 w-5 ${levelConfig.color} ${beastModeActive ? 'animate-pulse' : ''}`} />
            <span className={`font-black uppercase text-sm tracking-tight ${levelConfig.color}`}>
              {levelConfig.label}
            </span>
          </div>
          <button 
            onClick={() => setAudioEnabled(!audioEnabled)}
            className="text-muted-foreground hover:text-foreground"
          >
            {audioEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
        </div>
        
        {/* Meter Bar */}
        <div className="relative h-8 bg-secondary rounded-full overflow-hidden mb-2">
          <motion.div
            className={`absolute inset-y-0 left-0 ${levelConfig.bg} rounded-full`}
            initial={{ width: 0 }}
            animate={{ width: `${intensity}%` }}
            transition={{ type: "spring", stiffness: 100 }}
          />
          
          {/* Flame particles when high intensity */}
          {intensity >= 60 && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute bottom-0 text-xl"
                  style={{ left: `${Math.random() * 100}%` }}
                  animate={{
                    y: [0, -30, -60],
                    opacity: [1, 0.8, 0],
                    scale: [1, 1.2, 0.8]
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: i * 0.2
                  }}
                >
                  🔥
                </motion.div>
              ))}
            </div>
          )}
          
          {/* Percentage */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white font-mono font-black text-lg drop-shadow-lg">
              {Math.round(intensity)}%
            </span>
          </div>
        </div>
        
        {/* Message */}
        <AnimatePresence mode="wait">
          <motion.p
            key={message}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`text-center text-sm font-bold ${levelConfig.color}`}
          >
            {message || levelConfig.label}
          </motion.p>
        </AnimatePresence>
      </div>
      
      {/* Beast Mode Overlay */}
      <AnimatePresence>
        {beastModeActive && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 pointer-events-none"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ duration: 0.5, repeat: 3 }}
              className="text-center"
            >
              <div className="text-8xl mb-4">💀🔥💀</div>
              <h2 className="text-6xl font-black text-red-500 uppercase tracking-tighter">
                BEAST MODE!
              </h2>
              <p className="text-2xl text-white mt-2 font-bold">UNLEASH THE BEAST!</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Manual boost buttons (for testing/demo) */}
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => incrementIntensity(10)}
          className="flex-1 py-2 px-3 bg-primary/20 hover:bg-primary/40 rounded text-primary text-xs font-bold uppercase"
        >
          <Zap className="h-3 w-3 inline mr-1" /> +10
        </button>
        <button
          onClick={() => incrementIntensity(25)}
          className="flex-1 py-2 px-3 bg-orange-500/20 hover:bg-orange-500/40 rounded text-orange-500 text-xs font-bold uppercase"
        >
          <Flame className="h-3 w-3 inline mr-1" /> +25
        </button>
        <button
          onClick={() => setIntensity(100)}
          className="flex-1 py-2 px-3 bg-red-500/20 hover:bg-red-500/40 rounded text-red-500 text-xs font-bold uppercase"
        >
          💀 MAX
        </button>
      </div>
    </div>
  );
}

// Export a hook for use in workout components
export function useBeastMode() {
  const [intensity, setIntensity] = useState(0);
  const [beastModeActive, setBeastModeActive] = useState(false);
  
  const addIntensity = (amount) => {
    setIntensity(prev => {
      const newVal = Math.min(100, prev + amount);
      if (newVal >= 85 && !beastModeActive) {
        setBeastModeActive(true);
      }
      return newVal;
    });
  };
  
  const reset = () => {
    setIntensity(0);
    setBeastModeActive(false);
  };
  
  return { intensity, beastModeActive, addIntensity, reset, setIntensity };
}
